import { useCreateServiceAccount } from '@hooks/useServiceAccounts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ShowServiceAccountSecretDialog } from '@/components/settings/ShowServiceAccountSecretDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface CreateServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ExpiryPreset = '30' | '60' | '90' | 'never';

const PRESET_LABELS: Record<ExpiryPreset, string> = {
  '30': '30 days',
  '60': '60 days',
  '90': '90 days',
  never: 'Never',
};

const PRESETS: ExpiryPreset[] = ['30', '60', '90', 'never'];

function computeExpiresAt(preset: ExpiryPreset): string | undefined {
  if (preset === 'never') return undefined;
  const days = Number(preset);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function CreateServiceAccountDialog({
  open,
  onOpenChange,
}: CreateServiceAccountDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>('90');
  const [secret, setSecret] = useState<{ accessKeyId: string; secretKey: string } | null>(null);

  const createMutation = useCreateServiceAccount();

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setExpiryPreset('90');
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    createMutation.mutate(
      {
        name: trimmedName,
        description: description.trim() || undefined,
        expires_at: computeExpiresAt(expiryPreset),
      },
      {
        onSuccess: data => {
          if (data.service_account?.access_key_id && data.secret_key) {
            setSecret({
              accessKeyId: data.service_account.access_key_id,
              secretKey: data.secret_key,
            });
            onOpenChange(false);
          }
        },
      },
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Service Account</DialogTitle>
            <DialogDescription>
              Service accounts let your automation call the Compass API. The secret is shown only
              once on creation.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="sa-name">Name</Label>
              <Input
                id="sa-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. ci-playwright"
                autoFocus
                maxLength={64}
                disabled={createMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sa-description">Description</Label>
              <Textarea
                id="sa-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What this service account is used for"
                rows={2}
                maxLength={256}
                disabled={createMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>Expires</Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(preset => (
                  <Button
                    key={preset}
                    type="button"
                    variant={expiryPreset === preset ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExpiryPreset(preset)}
                    disabled={createMutation.isPending}
                  >
                    {PRESET_LABELS[preset]}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {expiryPreset === 'never'
                  ? 'This service account will never expire.'
                  : `Expires on ${new Date(computeExpiresAt(expiryPreset)!).toLocaleDateString()}.`}
              </p>
            </div>

            {createMutation.isError && (
              <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">Failed to create service account</p>
                    <p className="mt-1">
                      {getErrorMessage(createMutation.error) || 'Please try again.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!name.trim() || createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {secret && (
        <ShowServiceAccountSecretDialog
          open={secret !== null}
          onOpenChange={next => {
            if (!next) setSecret(null);
          }}
          accessKeyId={secret.accessKeyId}
          secretKey={secret.secretKey}
          title="Service account created"
          description="Copy the secret now — it will not be shown again."
        />
      )}
    </>
  );
}
